import { Dispatch, useEffect, useMemo, useRef } from 'react';

import { useGetTranslationsOnDemand } from '@api';
import { EPage } from '@types';
import { isObjectEmpty } from '@utils/general';

import { useErrorContext } from '../../errorService';
import { getMissingKeys, getPageTranslationsFromSessionStorage, setPageTranslationsToSessionStorage } from '../utils';

import { ELocalizationActions, ILocalizationActions, LocalizationAction } from './model';

export const useActions = (dispatch: Dispatch<LocalizationAction>): ILocalizationActions => {
  const pathRef = useRef<EPage | null>(null);
  const { request, response, loading } = useGetTranslationsOnDemand();
  const { callLogger } = useErrorContext();

  useEffect(() => {
    const page = pathRef.current;

    if (response && !loading && page) {
      setPageTranslationsToSessionStorage(response.data, callLogger, page);
      const translations = getPageTranslationsFromSessionStorage(page, callLogger);
      dispatch({ actionType: ELocalizationActions.UPDATE_TRANSLATIONS, payload: { translations } });
      return;
    }

    if (!loading) {
      dispatch({ actionType: ELocalizationActions.LOCALIZATION_STATE_READY });
    }
  }, [response, loading, dispatch, callLogger]);

  return useMemo(
    () => ({
      triggerTranslationLoad: async (page: EPage) => {
        // trigger loading of translations
        dispatch({ actionType: ELocalizationActions.LOCALIZATION_STATE_UNREADY });

        // get missing keys
        pathRef.current = page;
        const missingKeys = getMissingKeys(page, callLogger);

        // load missing keys
        if (missingKeys.length) {
          await request({ keys: missingKeys });
          return;
        }

        // translations are ready => get translations
        const translations = getPageTranslationsFromSessionStorage(page, callLogger);

        if (translations && !isObjectEmpty(translations) && !missingKeys.length) {
          dispatch({ actionType: ELocalizationActions.UPDATE_TRANSLATIONS, payload: { translations } });
        }
      },
    }),
    [dispatch, request, callLogger],
  );
};
